import { useState } from "react";
import { BsArrowRight, BsExclamationTriangleFill } from "react-icons/bs";
import { LiaPlusSolid, LiaMinusSolid } from "react-icons/lia";
import { Dropdown } from "primereact/dropdown";
import ValidatedInput from "../../components/ValidatedInput";
import AddressInput from "../../components/AddressInput";
import { useNavigate, useLocation } from "react-router-dom";
import { BsCheck2Circle } from "react-icons/bs";
import AuthService from "../../services/auth.service";
import ParcelOrderService from "../../services/parcel-order.service";
import { validators, validateForm } from "../../utils/validation";

export const CartPage = () => {
	const location = useLocation();

	// Get distance from location state (passed from home.js)
	const distance = location.state.distance || 'long';

	// Get parcel prices for selected parcel type
	const selectedParcel = location.state.parcels?.filter((parcel) => parcel.id.toString() === location.state.parcel_type.toString())[0];
	const parcelPrices = selectedParcel?.parcel_prices?.filter((parcel_price) => parcel_price.distance === distance) || [];


	// Determine initial delivery_type: use from navigation state if valid, otherwise default to first option
	const getInitialDeliveryType = () => {
		const deliveryTypeFromNav = location.state.delivery_type;

		// If delivery_type was passed from home page, validate it exists in parcel_prices
		if (deliveryTypeFromNav) {
			const isValid = parcelPrices.some(pp => pp.price.id.toString() === deliveryTypeFromNav.toString());
			if (isValid) {
				return deliveryTypeFromNav;
			}
		}

		// Default to first price option
		return parcelPrices[0]?.price?.id;
	};

	const [createOrderState, setCreateOrderState] = useState({
		departure_province: location.state.departure_province,
		destination_province: location.state.destination_province,
		departure_town: location.state.departure_town,
		pickup_location: location.state.pickup_location || "",
		drop_off_location: location.state.drop_off_location || "",
		destination_town: location.state.destination_town,
		collection_date: "",
		parcel_type: location.state.parcel_type,
		provinces: location.state.provinces,
		destination_provinces: [],
		delivery_type: getInitialDeliveryType(),
		quantity: 1,
		receiver_full_name: "",
		receiver_cellphone: "",
		parcel_description: "",
		number_of_items: "",
		collection_invoice_number: "",
		redirect_to: "/cart",
		departure_towns: location.state.departure_towns,
		destination_towns: location.state.destination_towns,
		parcels: location.state.parcels,
		parcel_prices: parcelPrices,
	});

	const [errors, setErrors] = useState({});
	const [touched, setTouched] = useState({});
	const [showValidationSummary, setShowValidationSummary] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const {
		departure_province,
		destination_province,
		departure_town,
		destination_town,
		parcel_type,
		provinces,
		departure_towns,
		destination_towns,
		delivery_type,
		quantity,
		pickup_location,
		drop_off_location,
		parcel_prices,
		collection_date,
	} = createOrderState;

	const navigate = useNavigate();

	const handleChange = (e) => {
		const { id, value } = e.target;
		setCreateOrderState({ ...createOrderState, [id]: value });

		// Clear validation summary when user starts fixing errors
		if (showValidationSummary) {
			setShowValidationSummary(false);
		}

		// Real-time validation for touched fields
		if (touched[id]) {
			validateSingleField(id, value);
		}
	};

	const handleBlur = (e) => {
		const { id, value } = e.target;
		setTouched({ ...touched, [id]: true });
		validateSingleField(id, value);
	};

	const validateSingleField = (fieldName, value) => {
		let error = '';

		switch (fieldName) {
			case 'collection_date':
				error = validators.required(value, 'Collection date') ||
				        validators.futureDate(value);
				break;
			case 'pickup_location':
				// Always validate pickup_location (for both short and long distance)
				error = validators.required(value, 'Pickup address') ||
				        validators.address(value);
				break;
			case 'drop_off_location':
				// Only validate drop_off_location for short distance orders
				if (distance === 'short') {
					error = validators.required(value, 'Drop-off address') ||
					        validators.address(value);
				}
				break;
			default:
				break;
		}

		setErrors(prev => ({ ...prev, [fieldName]: error }));
		return error;
	};

	const handleSelectChange = (event) => {
		const { id, value } = event.target;

		setCreateOrderState((prevState) => ({
			...prevState,
			[id]: value,
		}));
	};

	const handleSubmit = (event) => {
		event.preventDefault();

		const validationSchema = {
			collection_date: [
				(val) => validators.required(val, 'Collection date'),
				validators.futureDate
			],
			receiver_full_name: [
				(val) => validators.required(val, 'Receiver name'),
			],
			receiver_cellphone: [
				(val) => validators.required(val, 'Receiver phone number'),
			],
		};

		if (distance === 'long') {
			validationSchema.pickup_location = [
				(val) => validators.required(val, 'Collection address'),
				validators.address
			];
		} else {
			validationSchema.pickup_location = [
				(val) => validators.required(val, 'Pickup address'),
				validators.address
			];
			validationSchema.drop_off_location = [
				(val) => validators.required(val, 'Drop-off address'),
				validators.address
			];
		}

		const { errors: validationErrors, isValid } = validateForm(createOrderState, validationSchema);

		if (!isValid) {
			setErrors(validationErrors);
			const touchedFields = { collection_date: true, pickup_location: true };
			if (distance === 'short') {
				touchedFields.drop_off_location = true;
			}
			setTouched(touchedFields);
			setShowValidationSummary(true);

			const firstErrorField = Object.keys(validationErrors)[0];
			document.getElementById(firstErrorField)?.scrollIntoView({
				behavior: 'smooth',
				block: 'center'
			});
			return;
		}

		if (AuthService.isLogin()) {
			setSubmitting(true);
			ParcelOrderService.create(
				AuthService.getCurrentUser()?.user?.id,
				parcel_type,
				delivery_type,
				pickup_location,
				quantity,
				collection_date,
				departure_province,
				destination_province,
				departure_town,
				destination_town,
				drop_off_location,
				createOrderState.receiver_full_name,
				createOrderState.receiver_cellphone,
				createOrderState.parcel_description,
				createOrderState.number_of_items,
				createOrderState.collection_invoice_number
			).then(
				(data) => {
					const totalValue = parcel_prices.filter(
						(pp) => pp?.price?.id.toString() === delivery_type.toString()
					)[0]?.price?.value * quantity;
					localStorage.setItem("parcelin_pending_order", JSON.stringify({
						value: totalValue,
						reference: data.reference_number || "",
					}));
					window.location.assign(data.payment_url);
				},
				(error) => {
					console.log(error);
					setSubmitting(false);
				}
			);
		} else {
			navigate("/login", { state: createOrderState });
		}
	};

	const addQuantity = (event) => {
		event.preventDefault();
		setCreateOrderState((prevState) => ({
			...prevState,
			quantity: quantity + 1,
		}));
	};

	const subtractQuantity = (event) => {
		if (quantity - 1 == 0) {
			return;
		}

		event.preventDefault();
		setCreateOrderState((prevState) => ({
			...prevState,
			quantity: quantity - 1,
		}));
	};

	return (
		<div className="">
			<div className="w-full min-h- flex flex-col ">
				<div className="p-5 lg:py-20 flex flex-col gap-4 items-center">
					<form className="w-96  bg-white rounded-xl border border-purple-200 gap-4 p-4 justify-center items-center" onSubmit={handleSubmit}>
						{/* Validation Summary */}
						{showValidationSummary && Object.keys(errors).length > 0 && (
							<div className="w-full bg-red-50 border-2 border-red-500 rounded-lg p-4 mb-4 animate-slideDown">
								<div className="flex items-start gap-3">
									<BsExclamationTriangleFill className="text-red-500 mt-1 flex-shrink-0" size={20} />
									<div>
										<h3 className="font-bold text-red-800 mb-1">Please correct the errors below</h3>
										<ul className="text-sm text-red-700 list-disc list-inside">
											{Object.entries(errors).map(([field, error]) => (
												error && <li key={field}>{error}</li>
											))}
										</ul>
									</div>
								</div>
							</div>
						)}

						{/* Success indicator when all fields are valid */}
						{touched.collection_date &&
						 !errors.collection_date &&
						 collection_date &&
						 ((distance === 'long' && touched.pickup_location && !errors.pickup_location && pickup_location) ||
						  (distance === 'short' && touched.pickup_location && touched.drop_off_location &&
						   !errors.pickup_location && !errors.drop_off_location && pickup_location && drop_off_location)) && (
							<div className="w-full bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-4 animate-slideDown">
								<div className="flex items-center gap-3">
									<BsCheck2Circle className="text-green-500 flex-shrink-0" size={24} />
									<div>
										<h3 className="font-bold text-green-800">All details look good!</h3>
										<p className="text-sm text-green-700">You can proceed to payment</p>
									</div>
								</div>
							</div>
						)}

						{/* Only show province/town info for long distance orders */}
						{distance === 'long' && (
							<div className="flex justify-evenly w-full p-2">
								<div className="relative inline-block text-left w-[45%] ">
									<div className=" text-base font-bold text-left">{provinces.filter((province) => province.id.toString() === location.state.departure_province)[0]?.name}</div>
									<div className=" text-sm font-bold text-left">{departure_towns.filter((town) => town.id.toString() === location.state.departure_town)[0]?.name}</div>
								</div>

								<div className="relative inline-block text-left w-[45%] ">
									<div className=" text-base font-bold text-right">{provinces.filter((province) => province.id.toString() === location.state.destination_province)[0]?.name}</div>
									<div className=" text-sm font-bold text-right">{destination_towns.filter((town) => town.id.toString() === location.state.destination_town)[0]?.name}</div>
								</div>
							</div>
						)}

						{/* Editable addresses for short distance orders */}
						{distance === 'short' && (
							<div className="flex flex-col gap-3 p-2">
								<div className="text-sm font-semibold text-gray-800 text-center mb-2">Delivery Addresses</div>

								<div className="flex flex-col gap-2">
									<AddressInput
										id="pickup_location"
										name="pickup_location"
										value={createOrderState.pickup_location}
										onChange={handleChange}
										onBlur={handleBlur}
										label="Pickup Address"
										error={errors.pickup_location}
										touched={touched.pickup_location}
										showRequired={true}
										placeholder="Enter pickup address or use current location"
									/>
								</div>

								<div className="flex flex-col gap-2">
									<AddressInput
										id="drop_off_location"
										name="drop_off_location"
										value={createOrderState.drop_off_location}
										onChange={handleChange}
										onBlur={handleBlur}
										label="Drop-off Address"
										error={errors.drop_off_location}
										touched={touched.drop_off_location}
										showRequired={true}
										placeholder="Enter drop-off address or use current location"
									/>
								</div>
							</div>
						)}

						<div className="flex flex-col gap-2 p-2">
							<ValidatedInput
								id="collection_date"
								name="collection_date"
								type="date"
								value={createOrderState.collection_date}
								onChange={handleChange}
								onBlur={handleBlur}
								label="Collection Date"
								error={errors.collection_date}
								touched={touched.collection_date}
								showRequired={true}
								placeholder="mm/dd/yyyy"
							/>
						</div>

						{/* Only show pickup location input for long distance orders */}
						{distance === 'long' && (
							<div className="flex flex-col gap-3 p-2">
								<AddressInput
									id="pickup_location"
									name="pickup_location"
									value={createOrderState.pickup_location}
									onChange={handleChange}
									onBlur={handleBlur}
									label="Collection Address"
									error={errors.pickup_location}
									touched={touched.pickup_location}
									showRequired={true}
									placeholder="Enter address or use current location"
								/>
								<AddressInput
									id="drop_off_location"
									name="drop_off_location"
									value={createOrderState.drop_off_location}
									onChange={handleChange}
									onBlur={handleBlur}
									label="Drop-off Address"
									placeholder="Enter delivery address (optional)"
								/>
							</div>
						)}

						{/* Receiver details */}
						<div className="flex flex-col gap-3 p-2 border-t border-gray-100 pt-4">
							<div className="text-sm font-bold text-gray-800">Receiver Details</div>
							<div className="flex flex-col gap-2">
								<ValidatedInput
									id="receiver_full_name"
									name="receiver_full_name"
									type="text"
									value={createOrderState.receiver_full_name}
									onChange={handleChange}
									onBlur={handleBlur}
									label="Receiver Name"
									error={errors.receiver_full_name}
									touched={touched.receiver_full_name}
									showRequired={true}
									placeholder="Full name"
								/>
							</div>
							<div className="flex flex-col gap-2">
								<ValidatedInput
									id="receiver_cellphone"
									name="receiver_cellphone"
									type="tel"
									value={createOrderState.receiver_cellphone}
									onChange={handleChange}
									onBlur={handleBlur}
									label="Receiver Phone"
									error={errors.receiver_cellphone}
									touched={touched.receiver_cellphone}
									showRequired={true}
									placeholder="e.g. 0791234567"
								/>
							</div>
							<div className="flex flex-col gap-2">
								<ValidatedInput
									id="parcel_description"
									name="parcel_description"
									type="text"
									value={createOrderState.parcel_description}
									onChange={handleChange}
									label="Parcel Description"
									placeholder="e.g. Documents, Clothing"
								/>
							</div>
							<div className="flex flex-col gap-2">
								<ValidatedInput
									id="number_of_items"
									name="number_of_items"
									type="number"
									value={createOrderState.number_of_items}
									onChange={handleChange}
									label="Number of Items"
									placeholder="e.g. 5"
								/>
							</div>
							<div className="flex flex-col gap-2">
								<ValidatedInput
									id="collection_invoice_number"
									name="collection_invoice_number"
									type="text"
									value={createOrderState.collection_invoice_number}
									onChange={handleChange}
									label="Collection Invoice # (optional)"
									placeholder="e.g. INV-001"
								/>
							</div>
						</div>

						<div className="flex flex-col gap-2 p-2">
							<label className="block text-gray-700 text-sm font-semibold mb-2">
								Delivery Speed <span className="text-red-500">*</span>
							</label>
							<div className={`transition-all duration-200 ${delivery_type ? 'ring-2 ring-green-500 ring-opacity-50 rounded-lg' : ''}`}>
								<Dropdown
									value={delivery_type}
									onChange={(e) => handleSelectChange({ target: { id: "delivery_type", value: e.value } })}
									options={parcel_prices.map(price => ({
										label: `${price.price?.price_type?.name} - R${price.price?.value}`,
										value: price.price?.id
									}))}
									placeholder="Select delivery speed"
									className="w-full"
									style={{ borderRadius: "0.5rem" }}
								/>
							</div>
						</div>

						<div className="flex flex-col gap-3 p-2">
							<div className="flex justify-between items-center">
								<span className="font-semibold text-gray-700">Quantity</span>
								<div className="flex items-center gap-4 bg-white rounded-lg px-4 py-2 border border-gray-200">
									<button type="button" onClick={subtractQuantity} className="text-purple-600 hover:text-purple-700">
										<LiaMinusSolid size={20} />
									</button>
									<span className="font-bold text-lg w-8 text-center">{quantity}</span>
									<button type="button" onClick={addQuantity} className="text-purple-600 hover:text-purple-700">
										<LiaPlusSolid size={20} />
									</button>
								</div>
							</div>
							<div className="flex justify-between items-center pt-3 border-t border-gray-200">
								<span className="font-bold text-lg text-gray-800">Total Amount</span>
								<span className="font-bold text-2xl text-purple-600">
									R{parcel_prices.filter((parcel_price) => parcel_price?.price?.id.toString() === delivery_type.toString())[0]?.price?.value * quantity}
								</span>
							</div>
						</div>

						<div className="flex gap-4 justify-center items-center pt-5">
							<button
								type="submit"
								disabled={submitting}
								className={`w-full font-bold py-4 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 ${
									submitting ||
									(touched.collection_date && errors.collection_date) ||
									(distance === 'long' && touched.pickup_location && errors.pickup_location) ||
									(distance === 'short' && ((touched.pickup_location && errors.pickup_location) ||
									                          (touched.drop_off_location && errors.drop_off_location)))
										? 'bg-gray-400 cursor-not-allowed'
										: 'bg-purple-600 hover:bg-purple-700 hover:shadow-xl text-white'
								}`}
							>
								{submitting ? 'Redirecting to payment...' : 'Pay Now'}
								{!submitting && <BsArrowRight size={20} />}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};
